import type { CanvasSectionBlock } from '../components/sections/sectionRegistry';
import { findConnectedBlockNodeSummary } from '../components/flow/connectedBlocks';
import type { FlowDefinition, SelectedVase } from '../types';
import { findSectionLogicFlow } from './flowSectionHelpers';
import { buildVaseSelectionFlowPatch, findPrimaryVaseFlow } from './flows';

function findFlowForBlockId(
  flows: FlowDefinition[],
  blockId: string,
  preferredFlow?: FlowDefinition | null
) {
  if (preferredFlow?.nodes.some((entry) => entry.blockId === blockId)) {
    return preferredFlow;
  }
  return flows.find((flow) => flow.nodes.some((entry) => entry.blockId === blockId)) ?? null;
}

export function resolveSectionOrPageFlow(
  flows: FlowDefinition[] | null | undefined,
  sectionId: string | null | undefined
): FlowDefinition | null {
  if (!Array.isArray(flows)) return null;
  if (sectionId) {
    return findSectionLogicFlow(flows, sectionId);
  }
  return findPrimaryVaseFlow(flows);
}

export function resolveFlowForBlock(
  flows: FlowDefinition[] | null | undefined,
  args: {
    block: Pick<CanvasSectionBlock, 'id' | 'type'>;
    sectionId?: string | null;
  }
): FlowDefinition | null {
  if (!Array.isArray(flows)) return null;
  const preferredFlow = resolveSectionOrPageFlow(flows, args.sectionId);
  const blockOwnedFlow = findFlowForBlockId(flows, args.block.id, preferredFlow);
  if (blockOwnedFlow) return blockOwnedFlow;
  if (args.sectionId) return preferredFlow;
  const summary = findConnectedBlockNodeSummary(args.block, flows);
  if (summary) return summary.flow;
  return preferredFlow;
}

export function resolveProductCardSelectionTarget(
  flows: FlowDefinition[] | null | undefined,
  args: {
    block: Pick<CanvasSectionBlock, 'id' | 'type'>;
    sectionId?: string | null;
    vase: SelectedVase;
  }
): { flowId: string; values: Record<string, unknown> } | null {
  if (!Array.isArray(flows)) return null;

  const preferredFlow = resolveSectionOrPageFlow(flows, args.sectionId);
  const preferredFlowNode =
    preferredFlow?.nodes.find((entry) => entry.blockId === args.block.id) ?? null;
  const summary =
    preferredFlow && preferredFlowNode
      ? {
          flow: preferredFlow,
          node: preferredFlowNode,
        }
      : !args.sectionId
        ? findConnectedBlockNodeSummary(args.block, flows)
        : null;
  if (summary) {
    const productBinding = summary.node.outputs.find((binding) => binding.socketId === 'product');
    if (productBinding?.fieldId) {
      return {
        flowId: summary.flow.id,
        values: {
          [productBinding.fieldId]: args.vase,
        },
      };
    }
  }

  if (args.sectionId) {
    return null;
  }

  const fallbackFlow = resolveSectionOrPageFlow(flows, args.sectionId);
  if (!fallbackFlow) return null;
  return {
    flowId: fallbackFlow.id,
    values: buildVaseSelectionFlowPatch(args.vase),
  };
}
